
import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Tag, ArrowRight, Clock, Share2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Article } from "@/data/articlesData";
import OptimizedImage from "@/components/ui/optimized-image";

interface ArticleCardProps {
  article: Article;
  className?: string;
  showPartnerLogo?: boolean;
  partnerName?: string;
  priority?: boolean;
}

const ArticleCard2: React.FC<ArticleCardProps> = ({ 
  article, 
  className = "", 
  showPartnerLogo = false, 
  partnerName,
  priority = false
}) => {
  // Generate the path to the article
  const path = `/artikkelit/${article.slug}`;
  
  // Get the summary from article
  const summary = article.summary || article.description || "";
  
  // Use a default fallback image path that we know exists
  const fallbackImage = "/lovable-uploads/311dc1a4-c6fc-47bd-9033-022c2a566e0b.png";
  
  return (
    <Card className={`group overflow-hidden flex flex-col h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border border-gray-200 bg-white ${className}`}>
      {/* Fixed aspect ratio image container */}
      <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
        <OptimizedImage 
          src={article.image || fallbackImage} 
          alt={article.title}
          fill={true}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          fallbackSrc={fallbackImage}
          quality={80}
          objectFit="cover"
          priority={priority}
          containerClassName="w-full h-full"
          className="group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* Gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Share button overlay */}
        <button 
          className="absolute top-2 right-2 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white"
          onClick={(e) => {
            e.preventDefault();
            if (navigator.share) {
              navigator.share({
                title: article.title,
                url: `${window.location.origin}${path}`
              });
            }
          }}
          aria-label="Jaa artikkeli"
        >
          <Share2 size={16} className="text-gray-700" />
        </button>
      </div>
      
      {/* Card content with consistent spacing */}
      <div className="flex flex-col flex-grow p-4">
        {/* Meta information */}
        <div className="flex justify-between items-center mb-3">
          <span className="inline-flex items-center text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
            <Tag size={12} className="mr-1" aria-hidden="true" /> 
            {article.category}
          </span>
          <div className="flex items-center space-x-3 text-xs text-gray-500">
            <span className="flex items-center">
              <Calendar size={12} className="mr-1" aria-hidden="true" /> 
              {new Date(article.date).toLocaleDateString('fi-FI')}
            </span>
            {article.readTime && (
              <span className="flex items-center">
                <Clock size={12} className="mr-1" aria-hidden="true" /> 
                {article.readTime}
              </span>
            )}
          </div>
        </div>
        
        {/* Title */}
        <CardTitle className="text-lg font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors duration-200">
          <Link to={path} className="hover:no-underline">
            {article.title}
          </Link>
        </CardTitle>
        
        {/* Description */}
        <CardDescription className="text-gray-600 text-sm line-clamp-3 mb-4 flex-grow leading-relaxed">
          {summary}
        </CardDescription>
        
        {/* Read more link */}
        <Link 
          to={path}
          className="inline-flex items-center text-blue-600 font-medium text-sm hover:text-blue-700 transition-colors duration-200 group/link"
          aria-label={`Lue artikkeli: ${article.title}`}
        >
          Lue artikkeli
          <ArrowRight size={14} className="ml-1 group-hover/link:translate-x-1 transition-transform duration-200" />
        </Link>
      </div>
    </Card>
  );
};

export default ArticleCard2;
